import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Quaternion, type OrthographicCamera } from 'three';
import { ORBIT_AXIS, WORLD_UP } from '@/lib/threeScene/orbitAxis';

/**
 * A subclass of stock OrbitControls that pins the orbit-axis quaternion to
 * `LPS.Superior` instead of letting it follow `camera.up`.
 *
 * Why this exists
 * ---------------
 * Three.js OrbitControls maintains an internal quaternion (`_quat`) that maps
 * the *orbit axis* into the world-up direction `(0, 1, 0)`. Three.js sets this
 * once at construction from `camera.up`, then never updates it. That's a
 * problem in two ways for us:
 *
 *   1. **Wrong default.** Three.js's default `camera.up` is `(0, 1, 0)`. Our
 *      planner uses LPS axes, where the patient's vertical axis is Superior
 *      (`(0, 0, 1)`). So out-of-the-box OrbitControls would orbit around the
 *      +Y axis, not the spine.
 *
 *   2. **`camera.up` is overloaded.** Our snap-to-view presets reassign
 *      `camera.up` for *screen orientation*: e.g. the Bottom preset sets
 *      `camera.up = LPS.Anterior` so that Anterior appears at the top of the
 *      screen when looking up at the patient. We do **not** want this to
 *      relocate the orbit gimbal — the user expects to keep orbiting around
 *      the spine no matter which snap view they jumped to.
 *
 * Why we touch `_quat` / `_quatInverse` directly
 * ----------------------------------------------
 * Three.js does not expose a public API for "use a specific axis as the orbit
 * gimbal." These two underscore-prefixed properties are the only way to do
 * it. They are present in OrbitControls's source for at least the `0.181.x`
 * line that we pin in package.json. The contract is verified at construction
 * by `assertOrbitInternals` below — a Three.js upgrade that renames or
 * removes them will throw a loud, clear error at viewport-mount time rather
 * than silently producing wrong orbit behaviour.
 *
 * Tested against three@0.181.0.
 */
class LpsOrbitControls extends OrbitControls {
    // _quat / _quatInverse exist on stock OrbitControls but aren't in its
    // TypeScript declaration; re-declare them here with `declare` (emits
    // no JS, just narrows the type).
    declare _quat: Quaternion;
    declare _quatInverse: Quaternion;

    constructor(
        camera: OrthographicCamera,
        domElement: HTMLElement,
    ) {
        super(camera, domElement);
        assertOrbitInternals(this);
        this.pinOrbitAxis();
    }

    /**
     * Pin the orbit axis to `ORBIT_AXIS` (LPS.Superior). Idempotent; safe to
     * call repeatedly. Called once in the constructor — the stock OrbitControls
     * never touches `_quat` again after its own constructor, so a single pin
     * is sufficient for the controls' lifetime.
     */
    private pinOrbitAxis(): void {
        this._quat.setFromUnitVectors(ORBIT_AXIS, WORLD_UP);
        this._quatInverse.copy(this._quat).invert();
    }
}

let internalsChecked = false;

/**
 * Runtime contract check: verify on the first real instance we construct that
 * the Three.js internals we depend on still exist and have the expected shape.
 * Cheaper than per-frame, but still fails loud and clear if a future Three.js
 * upgrade breaks the contract.
 */
function assertOrbitInternals(instance: LpsOrbitControls): void {
    if (internalsChecked) return;
    if (
        !(instance._quat instanceof Quaternion) ||
        !(instance._quatInverse instanceof Quaternion)
    ) {
        throw new Error(
            'OrbitControls internal contract broken: _quat / _quatInverse are not Quaternions. ' +
                'The pinned Three.js version may have changed — see planner/src/lib/threeScene/orbitControls.ts.',
        );
    }
    internalsChecked = true;
}

export function orbitControls(
    canvas: HTMLCanvasElement,
    camera: OrthographicCamera,
): OrbitControls {
    const controls = new LpsOrbitControls(camera, canvas);
    controls.screenSpacePanning = true;
    return controls;
}
